import { useMemo } from "react";
import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { ReviewPresenceEntry, presenceKey } from "../types";
import { reviewTokens } from "../theme";

interface Props {
  presence: ReviewPresenceEntry[];
}

const initialsOf = (entry: ReviewPresenceEntry) => {
  const name = entry.displayName || entry.audienceKey || "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const AVATAR_TINTS = [
  { bg: "#E0F2FE", fg: "#0369A1" },
  { bg: "#DCFCE7", fg: "#15803D" },
  { bg: "#FEF3C7", fg: "#B45309" },
  { bg: "#FCE7F3", fg: "#BE185D" },
  { bg: "#EDE9FE", fg: "#6D28D9" },
  { bg: "#E0E7FF", fg: "#4338CA" },
  { bg: "#FFE4E6", fg: "#BE123C" },
];

const tintFor = (key: string) => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  }
  return AVATAR_TINTS[hash % AVATAR_TINTS.length];
};

const LiveDot = () => (
  <Box
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      bgcolor: "#10B981",
      border: `1.5px solid ${reviewTokens.bgPaper}`,
      boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.6)",
      animation: "presence-pulse 2s ease-out infinite",
      "@keyframes presence-pulse": {
        "0%": { boxShadow: "0 0 0 0 rgba(16, 185, 129, 0.5)" },
        "70%": { boxShadow: "0 0 0 6px rgba(16, 185, 129, 0)" },
        "100%": { boxShadow: "0 0 0 0 rgba(16, 185, 129, 0)" },
      },
    }}
  />
);

const TooltipBody = ({ entries }: { entries: ReviewPresenceEntry[] }) => (
  <Stack spacing={0.5} sx={{ py: 0.25 }}>
    <Typography
      variant="caption"
      sx={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        opacity: 0.7,
      }}
    >
      Currently viewing
    </Typography>
    {entries.map((p) => {
      const tint = tintFor(presenceKey(p));
      const name = p.displayName || p.audienceKey || "Anonymous";
      return (
        <Stack
          key={presenceKey(p)}
          direction="row"
          alignItems="center"
          spacing={0.75}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: "50%",
              bgcolor: tint.bg,
              color: tint.fg,
              fontSize: 9,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {initialsOf(p)}
          </Box>
          <Typography variant="caption" sx={{ fontSize: 12 }}>
            {name}
          </Typography>
        </Stack>
      );
    })}
  </Stack>
);

export function PresenceBadge({ presence }: Props) {
  const { t } = useTranslation();
  const unique = useMemo(() => {
    const seen = new Map<string, ReviewPresenceEntry>();
    for (const p of presence) {
      const k = presenceKey(p);
      if (!seen.has(k)) seen.set(k, p);
    }
    return Array.from(seen.values());
  }, [presence]);

  if (unique.length === 0) return null;

  return (
    <Tooltip title={<TooltipBody entries={unique} />} arrow placement="bottom">
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        data-testid="presence-badge"
        sx={{
          height: 28,
          pl: 0.5,
          pr: 1,
          borderRadius: 999,
          bgcolor: reviewTokens.slate50,
          border: `1px solid ${reviewTokens.border}`,
          cursor: "default",
          transition: "background-color 0.15s ease, border-color 0.15s ease",
          "&:hover": {
            bgcolor: reviewTokens.bgPaper,
            borderColor: reviewTokens.borderStrong,
          },
        }}
      >
        <Badge
          overlap="circular"
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          badgeContent={<LiveDot />}
          sx={{
            "& .MuiBadge-badge": { padding: 0, minWidth: 0, height: "auto" },
          }}
        >
          <AvatarGroup
            max={3}
            sx={{
              "& .MuiAvatar-root": {
                width: 20,
                height: 20,
                fontSize: 9,
                fontWeight: 700,
                border: `1.5px solid ${reviewTokens.bgPaper}`,
                marginLeft: "-6px",
                "&:first-of-type": { marginLeft: 0 },
              },
            }}
          >
            {unique.map((p) => {
              const tint = tintFor(presenceKey(p));
              return (
                <Avatar
                  key={presenceKey(p)}
                  sx={{ bgcolor: tint.bg, color: tint.fg }}
                >
                  {initialsOf(p)}
                </Avatar>
              );
            })}
          </AvatarGroup>
        </Badge>
        <Typography
          variant="caption"
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: "text.secondary",
            letterSpacing: 0,
          }}
        >
          {t(messages.Viewing(), { count: unique.length })}
        </Typography>
      </Stack>
    </Tooltip>
  );
}
